import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef } from "react";

import { AuthShell } from "@/components/AuthShell";
import { TeamInviteForm } from "@/components/TeamInviteForm";

import { useCurrentUser } from "../../auth/useCurrentUser";
import { Button } from "../../components/ui/button";

/**
 * Flow A step 6 — "Registration Complete / Welcome" (CHP-207, CHP-295).
 *
 * Reached by a Program Owner two ways: the org's SIGNING PO lands here after
 * accepting the T&Cs (terms.tsx routes them here), and — CHP-1021 — an
 * ADDITIONAL PO of an already-registered org lands here straight from the
 * identity step (they inherit the org's T&C acceptance and never see a Terms
 * screen). BDMs / admins / managers go straight to /dashboard. Confirms the
 * account and embeds the shared team-invite form (email + access level per row)
 * so the PO can invite their team on the spot — skippable via "Go to my
 * dashboard" (design review CHP-295; the org-details box was removed by the
 * same review).
 *
 * Lives OUTSIDE ProtectedRoute like the rest of the wizard, and — unlike the
 * earlier steps — does NOT bounce tc-accepted users (the signing PO just
 * accepted; an additional PO's acceptance was stamped on identity submit).
 */
export function OnboardingCompleteStep() {
  const me = useCurrentUser();
  const navigate = useNavigate();

  // CHP-1105: resolve the redirect as a value + fire it from a ref-guarded
  // effect instead of returning a render-time <Navigate>, which flashes a
  // frame of the wrong route and can re-navigate on every re-render. `null`
  // means "stay put". Mirrors ProtectedRoute.
  const redirectTarget: string | null =
    me.isAuthLoading || me.isLoading ? null : !me.isAuthenticated ? "/login" : null;

  const redirected = useRef(false);
  useEffect(() => {
    if (redirectTarget === null || redirected.current) return;
    redirected.current = true;
    void navigate({ to: redirectTarget, replace: true });
  }, [redirectTarget, navigate]);

  if (me.isAuthLoading || me.isLoading) {
    return <p role="status">Loading…</p>;
  }
  // Redirect pending → loader, never a flash of the wrong route.
  if (redirectTarget !== null) {
    return <p role="status">Loading…</p>;
  }

  const u = me.data;
  // PO = channel_partner with is_org_admin !== false (null/true default to
  // PO for back-compat). Only POs manage a team, so only they get the
  // embedded invite form; everyone else just sees the dashboard CTA.
  const isPo = u?.role === "channel_partner" && u.is_org_admin !== false;
  const orgName = u?.organization?.company_name ?? u?.company_name ?? null;
  const orgId = u?.organization?.id ?? null;

  return (
    <AuthShell
      headingId="complete-heading"
      showLogo
      overline="Registration complete"
      overlineClassName="text-sm font-semibold uppercase tracking-[0.18em] text-primary"
      heading="Welcome to the Program!"
      maxWidthClassName="max-w-lg"
      wrapperClassName="min-h-screen flex items-center justify-center px-4 py-10"
      subheading={
        <>
          Your account is set up and ready.{" "}
          {isPo
            ? "Invite your team to get started, or head straight to your dashboard."
            : "Head to your dashboard to get started."}
        </>
      }
    >
      {isPo && me.accessToken ? (
        <section aria-label="Invite your team" className="space-y-3 border-t pt-5">
          <h2 className="text-sm font-semibold">Invite your team</h2>
          <TeamInviteForm token={me.accessToken} orgName={orgName} orgId={orgId} />
        </section>
      ) : null}

      <div className="flex flex-col gap-3 sm:flex-row">
        <Button
          asChild
          size="lg"
          variant={isPo ? "outline" : "default"}
          className="w-full sm:flex-1"
        >
          <Link to="/dashboard">Go to my dashboard</Link>
        </Button>
      </div>
    </AuthShell>
  );
}
